import React, { useState } from "react";
import {
  Header,
  SpaceBetween,
  Box,
  Button,
  ButtonDropdown,
  Table,
  TextFilter,
  Tabs,
  Container,
  Badge,
  Select,
  FormField,
  StatusIndicator,
  Multiselect,
  Modal,
  Input,
  ExpandableSection,
  Pagination,
} from "@cloudscape-design/components";
import "./PermissionManagement.css";

// ─── Types ───────────────────────────────────────────────────────────────────
type RoleName = "System Admin" | "Administrator" | "Viewer" | "Update Program" | "Edit User";
type SystemName = "MSA" | "Atlas" | "Lighthouse";

// ─── Mock Data: Systems ─────────────────────────────────────────────────────
const ALL_SYSTEMS: { label: string; value: string }[] = [
  { label: "MSA", value: "MSA" },
  { label: "Atlas", value: "Atlas" },
  { label: "Lighthouse", value: "Lighthouse" },
];

const CURRENT_SYSTEM: SystemName = "MSA";

// ─── Mock Data: Role Definitions ────────────────────────────────────────────
interface FeaturePermission {
  feature: string;
  actions: { action: string; allowed: boolean }[];
}

interface RoleDefinition {
  id: string;
  system: SystemName;
  roleName: RoleName;
  description: string;
  type: "system" | "custom";
  status: "active" | "inactive";
  permissions: FeaturePermission[];
}

const SYSTEM_ROLE_PERMISSIONS_FULL: FeaturePermission[] = [
  { feature: "Executive Overview", actions: [{ action: "View", allowed: true }] },
  { feature: "Monitor & Alert", actions: [{ action: "View", allowed: true }, { action: "Configure", allowed: true }] },
  { feature: "Intelligent Analysis", actions: [{ action: "View", allowed: true }, { action: "Run", allowed: true }] },
  { feature: "Report Agent", actions: [{ action: "View", allowed: true }, { action: "Generate", allowed: true }] },
  { feature: "Process Optimization", actions: [{ action: "View", allowed: true }, { action: "Execute", allowed: true }] },
  { feature: "Configuration Setting", actions: [{ action: "View List", allowed: true }, { action: "Onboard", allowed: true }, { action: "Update", allowed: true }, { action: "Enable/Disable", allowed: true }] },
  { feature: "Permission Management", actions: [{ action: "View", allowed: true }, { action: "Manage", allowed: true }] },
];

const SYSTEM_ROLE_PERMISSIONS_VIEWER: FeaturePermission[] = [
  { feature: "Executive Overview", actions: [{ action: "View", allowed: true }] },
  { feature: "Monitor & Alert", actions: [{ action: "View", allowed: true }, { action: "Configure", allowed: false }] },
  { feature: "Intelligent Analysis", actions: [{ action: "View", allowed: true }, { action: "Run", allowed: false }] },
  { feature: "Report Agent", actions: [{ action: "View", allowed: true }, { action: "Generate", allowed: false }] },
  { feature: "Process Optimization", actions: [{ action: "View", allowed: true }, { action: "Execute", allowed: false }] },
  { feature: "Configuration Setting", actions: [{ action: "View List", allowed: true }, { action: "Onboard", allowed: false }, { action: "Update", allowed: false }, { action: "Enable/Disable", allowed: false }] },
  { feature: "Permission Management", actions: [{ action: "View", allowed: true }, { action: "Manage", allowed: false }] },
];

const MOCK_ROLE_DEFINITIONS: RoleDefinition[] = [
  // MSA system roles
  { id: "rd-msa-001", system: "MSA", roleName: "System Admin", description: "Full system access including all features, configuration, and permission management.", type: "system", status: "active", permissions: SYSTEM_ROLE_PERMISSIONS_FULL },
  { id: "rd-msa-002", system: "MSA", roleName: "Administrator", description: "All features and actions. Can assign non-admin roles within own data scope.", type: "system", status: "active", permissions: SYSTEM_ROLE_PERMISSIONS_FULL },
  { id: "rd-msa-003", system: "MSA", roleName: "Viewer", description: "View-only access to all features. Cannot modify or configure anything.", type: "system", status: "active", permissions: SYSTEM_ROLE_PERMISSIONS_VIEWER },
  { id: "rd-msa-004", system: "MSA", roleName: "Update Program", description: "Can view program list and update programs in Configuration Setting only.", type: "custom", status: "active", permissions: [
    { feature: "Executive Overview", actions: [{ action: "View", allowed: false }] },
    { feature: "Monitor & Alert", actions: [{ action: "View", allowed: false }, { action: "Configure", allowed: false }] },
    { feature: "Intelligent Analysis", actions: [{ action: "View", allowed: false }, { action: "Run", allowed: false }] },
    { feature: "Report Agent", actions: [{ action: "View", allowed: false }, { action: "Generate", allowed: false }] },
    { feature: "Process Optimization", actions: [{ action: "View", allowed: false }, { action: "Execute", allowed: false }] },
    { feature: "Configuration Setting", actions: [{ action: "View List", allowed: true }, { action: "Onboard", allowed: false }, { action: "Update", allowed: true }, { action: "Enable/Disable", allowed: false }] },
    { feature: "Permission Management", actions: [{ action: "View", allowed: false }, { action: "Manage", allowed: false }] },
  ]},
  { id: "rd-msa-005", system: "MSA", roleName: "Edit User", description: "Can only edit existing user assignments in Permission Management.", type: "custom", status: "inactive", permissions: [
    { feature: "Executive Overview", actions: [{ action: "View", allowed: false }] },
    { feature: "Monitor & Alert", actions: [{ action: "View", allowed: false }, { action: "Configure", allowed: false }] },
    { feature: "Intelligent Analysis", actions: [{ action: "View", allowed: false }, { action: "Run", allowed: false }] },
    { feature: "Report Agent", actions: [{ action: "View", allowed: false }, { action: "Generate", allowed: false }] },
    { feature: "Process Optimization", actions: [{ action: "View", allowed: false }, { action: "Execute", allowed: false }] },
    { feature: "Configuration Setting", actions: [{ action: "View List", allowed: false }, { action: "Onboard", allowed: false }, { action: "Update", allowed: false }, { action: "Enable/Disable", allowed: false }] },
    { feature: "Permission Management", actions: [{ action: "View", allowed: true }, { action: "Edit", allowed: true }] },
  ]},
  // Atlas system roles
  { id: "rd-atlas-001", system: "Atlas", roleName: "System Admin", description: "Full system access including all features, configuration, and permission management.", type: "system", status: "active", permissions: SYSTEM_ROLE_PERMISSIONS_FULL },
  { id: "rd-atlas-002", system: "Atlas", roleName: "Administrator", description: "All features and actions. Can assign non-admin roles within own data scope.", type: "system", status: "active", permissions: SYSTEM_ROLE_PERMISSIONS_FULL },
  { id: "rd-atlas-003", system: "Atlas", roleName: "Viewer", description: "View-only access to all features. Cannot modify or configure anything.", type: "system", status: "active", permissions: SYSTEM_ROLE_PERMISSIONS_VIEWER },
  { id: "rd-atlas-004", system: "Atlas", roleName: "Edit User", description: "Can only edit existing user assignments in Permission Management.", type: "custom", status: "active", permissions: [
    { feature: "Executive Overview", actions: [{ action: "View", allowed: false }] },
    { feature: "Monitor & Alert", actions: [{ action: "View", allowed: false }, { action: "Configure", allowed: false }] },
    { feature: "Intelligent Analysis", actions: [{ action: "View", allowed: false }, { action: "Run", allowed: false }] },
    { feature: "Report Agent", actions: [{ action: "View", allowed: false }, { action: "Generate", allowed: false }] },
    { feature: "Process Optimization", actions: [{ action: "View", allowed: false }, { action: "Execute", allowed: false }] },
    { feature: "Configuration Setting", actions: [{ action: "View List", allowed: false }, { action: "Onboard", allowed: false }, { action: "Update", allowed: false }, { action: "Enable/Disable", allowed: false }] },
    { feature: "Permission Management", actions: [{ action: "View", allowed: true }, { action: "Edit", allowed: true }] },
  ]},
  // Lighthouse system roles
  { id: "rd-lh-001", system: "Lighthouse", roleName: "System Admin", description: "Full system access including all features, configuration, and permission management.", type: "system", status: "active", permissions: SYSTEM_ROLE_PERMISSIONS_FULL },
  { id: "rd-lh-002", system: "Lighthouse", roleName: "Administrator", description: "All features and actions. Can assign non-admin roles within own data scope.", type: "system", status: "active", permissions: SYSTEM_ROLE_PERMISSIONS_FULL },
  { id: "rd-lh-003", system: "Lighthouse", roleName: "Viewer", description: "View-only access to all features. Cannot modify or configure anything.", type: "system", status: "active", permissions: SYSTEM_ROLE_PERMISSIONS_VIEWER },
];

// ─── Mock Data: User Assignments (role + data scope combined) ────────────────
interface UserAssignment {
  id: string;
  user: string;
  system: SystemName;
  role: RoleName;
  operations: string[];
  cm: string[];
  locations: string[];
  programs: string[];
  assignedDate: string;
  assignedBy: string;
  status: "active" | "inactive";
}

const MOCK_USER_ASSIGNMENTS: UserAssignment[] = [
  {
    id: "ua-001",
    user: "john.doe",
    system: "MSA",
    role: "Administrator",
    operations: ["North Ops", "South Ops"],
    cm: ["Foxconn", "Pegatron"],
    locations: ["All"],
    programs: ["All"],
    assignedDate: "2024-02-01",
    assignedBy: "sarah.chen",
    status: "active",
  },
  {
    id: "ua-002",
    user: "lisa.wang",
    system: "MSA",
    role: "Administrator",
    operations: ["North Ops", "South Ops", "Ring"],
    cm: ["Pegatron", "Compal"],
    locations: ["All"],
    programs: ["All"],
    assignedDate: "2024-02-05",
    assignedBy: "sarah.chen",
    status: "active",
  },
  {
    id: "ua-003",
    user: "jane.smith",
    system: "Atlas",
    role: "Viewer",
    operations: ["North Ops"],
    cm: ["Compal"],
    locations: ["Kunshan", "Chongqing"],
    programs: ["All"],
    assignedDate: "2024-02-10",
    assignedBy: "john.doe",
    status: "active",
  },
  {
    id: "ua-004",
    user: "mike.wilson",
    system: "Atlas",
    role: "Viewer",
    operations: ["Ring"],
    cm: ["Flex"],
    locations: ["All"],
    programs: ["Ring Doorbell Pro", "Blink Camera"],
    assignedDate: "2024-03-05",
    assignedBy: "lisa.wang",
    status: "active",
  },
  {
    id: "ua-005",
    user: "alex.kumar",
    system: "MSA",
    role: "Update Program",
    operations: ["South Ops"],
    cm: ["Foxconn"],
    locations: ["Shenzhen"],
    programs: ["Echo Show 10", "Echo Dot 6"],
    assignedDate: "2024-03-10",
    assignedBy: "john.doe",
    status: "inactive",
  },
  {
    id: "ua-006",
    user: "tom.zhang",
    system: "Lighthouse",
    role: "Edit User",
    operations: ["North Ops"],
    cm: ["Foxconn", "Compal"],
    locations: ["All"],
    programs: ["All"],
    assignedDate: "2024-04-01",
    assignedBy: "john.doe",
    status: "active",
  },
  {
    id: "ua-007",
    user: "david.liu",
    system: "MSA",
    role: "Viewer",
    operations: ["North Ops"],
    cm: ["Foxconn"],
    locations: ["Shenzhen"],
    programs: ["All"],
    assignedDate: "2024-04-15",
    assignedBy: "tom.zhang",
    status: "active",
  },
  {
    id: "ua-008",
    user: "sarah.lin",
    system: "MSA",
    role: "Edit User",
    operations: ["North Ops"],
    cm: ["Foxconn"],
    locations: ["All"],
    programs: ["All"],
    assignedDate: "2024-05-01",
    assignedBy: "john.doe",
    status: "active",
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const getRoleBadgeColor = (role: RoleName): "red" | "blue" | "grey" | "green" => {
  switch (role) {
    case "System Admin": return "red";
    case "Administrator": return "blue";
    case "Update Program": return "green";
    case "Edit User": return "green";
    case "Viewer": return "grey";
    default: return "grey";
  }
};

const getRoleAbbreviation = (role: string): string => {
  switch (role) {
    case "System Admin": return "S";
    case "Administrator": return "A";
    case "Viewer": return "V";
    case "Update Program": return "U";
    case "Edit User": return "E";
    default: return role.charAt(0);
  }
};

const CURRENT_USER_ROLE: RoleName = "System Admin";

const getAssignableRoles = (currentUserRole: RoleName) => {
  const assignableRoles = [
    { label: "Administrator", value: "Administrator", description: "All features & actions, can assign roles within scope" },
    { label: "Viewer", value: "Viewer", description: "Read-only access to dashboards" },
    { label: "Update Program", value: "Update Program", description: "View list and update programs in Configuration Setting" },
    { label: "Edit User", value: "Edit User", description: "Can only edit user assignments in Permission Management" },
  ];
  switch (currentUserRole) {
    case "System Admin": return assignableRoles;
    case "Administrator": return assignableRoles.filter((r) => r.value !== "Administrator");
    default: return [];
  }
};

const ALL_OPERATIONS = [
  { label: "All", value: "All", description: "All operations" },
  { label: "North Ops", value: "North Ops" },
  { label: "South Ops", value: "South Ops" },
  { label: "Ring", value: "Ring" },
];

const ALL_CMS = [
  { label: "All", value: "All", description: "All CMs" },
  { label: "Foxconn", value: "Foxconn" },
  { label: "Pegatron", value: "Pegatron" },
  { label: "Compal", value: "Compal" },
  { label: "Flex", value: "Flex" },
];

const ALL_LOCATIONS = [
  { label: "All", value: "All", description: "All locations" },
  { label: "Shenzhen", value: "Shenzhen" },
  { label: "Taipei", value: "Taipei" },
  { label: "Kunshan", value: "Kunshan" },
  { label: "Chongqing", value: "Chongqing" },
  { label: "Zhuhai", value: "Zhuhai" },
  { label: "Suzhou", value: "Suzhou" },
];

const ALL_PROGRAMS = [
  { label: "All", value: "All", description: "All programs" },
  { label: "Echo Dot 6", value: "Echo Dot 6" },
  { label: "Echo Show 10", value: "Echo Show 10" },
  { label: "Fire TV Stick 4K", value: "Fire TV Stick 4K" },
  { label: "Kindle Paperwhite", value: "Kindle Paperwhite" },
  { label: "Ring Doorbell Pro", value: "Ring Doorbell Pro" },
  { label: "Blink Camera", value: "Blink Camera" },
];

// ─── Mock Data: By Operation/CM/Location View ──────────────────────────────
interface OclViewItem {
  id: string;
  system: SystemName;
  operation: string;
  cm: string;
  location: string;
  users: { user: string; role: RoleName; programs: string[] }[];
  status: "active" | "inactive";
}

const MOCK_OCL_VIEW: OclViewItem[] = [
  {
    id: "ocl-001",
    system: "MSA",
    operation: "North Ops",
    cm: "Foxconn",
    location: "All Locations",
    users: [
      { user: "john.doe", role: "Administrator", programs: ["All"] },
    ],
    status: "active",
  },
  {
    id: "ocl-002",
    system: "MSA",
    operation: "South Ops",
    cm: "Foxconn",
    location: "Shenzhen",
    users: [
      { user: "john.doe", role: "Administrator", programs: ["All"] },
      { user: "alex.kumar", role: "Update Program", programs: ["Echo Show 10", "Echo Dot 6"] },
    ],
    status: "active",
  },
  {
    id: "ocl-003",
    system: "MSA",
    operation: "North Ops",
    cm: "Pegatron",
    location: "All Locations",
    users: [
      { user: "john.doe", role: "Administrator", programs: ["All"] },
      { user: "lisa.wang", role: "Administrator", programs: ["All"] },
    ],
    status: "active",
  },
  {
    id: "ocl-004",
    system: "Atlas",
    operation: "North Ops",
    cm: "Compal",
    location: "Kunshan",
    users: [
      { user: "jane.smith", role: "Viewer", programs: ["All"] },
      { user: "lisa.wang", role: "Administrator", programs: ["All"] },
    ],
    status: "active",
  },
  {
    id: "ocl-005",
    system: "Atlas",
    operation: "Ring",
    cm: "Flex",
    location: "All Locations",
    users: [
      { user: "mike.wilson", role: "Viewer", programs: ["Ring Doorbell Pro", "Blink Camera"] },
    ],
    status: "inactive",
  },
  {
    id: "ocl-006",
    system: "Lighthouse",
    operation: "South Ops",
    cm: "Pegatron",
    location: "All Locations",
    users: [
      { user: "lisa.wang", role: "Administrator", programs: ["All"] },
    ],
    status: "active",
  },
];

// ─── View mode type ─────────────────────────────────────────────────────────
type DataViewMode = "by-user" | "by-ocl";

// ─── Users & Roles Tab ──────────────────────────────────────────────────────
const PAGE_SIZE = 10;

const UsersAndRolesTab: React.FC<{ simulatedRole: RoleName | null; simulatedUser: UserAssignment | null | undefined; selectedSystem: string }> = ({ simulatedRole, simulatedUser, selectedSystem }) => {
  const [viewMode, setViewMode] = useState<DataViewMode>("by-user");
  const [filterText, setFilterText] = useState("");
  const [selectedItems, setSelectedItems] = useState<UserAssignment[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [oclCurrentPage, setOclCurrentPage] = useState(1);

  // Assign Role Modal
  const [assignVisible, setAssignVisible] = useState(false);
  const [assignSystem, setAssignSystem] = useState<any>(
    selectedSystem === "All" ? null : { label: selectedSystem, value: selectedSystem }
  );
  const [assignUserType, setAssignUserType] = useState<any>({ label: "Amazon User", value: "amazon-user" });
  const [assignUser, setAssignUser] = useState("");
  const [assignRoles, setAssignRoles] = useState<readonly any[]>([]);
  const [assignOperations, setAssignOperations] = useState<readonly any[]>([]);
  const [assignCm, setAssignCm] = useState<readonly any[]>([]);
  const [assignLocations, setAssignLocations] = useState<readonly any[]>([]);
  const [assignPrograms, setAssignPrograms] = useState<readonly any[]>([]);

  // Edit Modal
  const [editVisible, setEditVisible] = useState(false);
  const [editRole, setEditRole] = useState<any>(null);
  const [editOperations, setEditOperations] = useState<readonly any[]>([]);
  const [editCm, setEditCm] = useState<readonly any[]>([]);
  const [editLocations, setEditLocations] = useState<readonly any[]>([]);
  const [editPrograms, setEditPrograms] = useState<readonly any[]>([]);

  // OCL Add User (within edit modal)
  const [oclAddUserName, setOclAddUserName] = useState("");
  const [oclAddUserRole, setOclAddUserRole] = useState<any>(null);
  const [oclAddedUsers, setOclAddedUsers] = useState<{ user: string; role: string }[]>([]);

  // Delete Modal
  const [deleteVisible, setDeleteVisible] = useState(false);

  // Status Change Modal
  const [statusVisible, setStatusVisible] = useState(false);
  const [statusAction, setStatusAction] = useState<"activate" | "deactivate">("activate");

  // OCL view selection
  const [selectedOclItems, setSelectedOclItems] = useState<OclViewItem[]>([]);

  const canManagePermissions = !simulatedRole || simulatedRole === "System Admin" || simulatedRole === "Administrator";
  const canEditOnly = simulatedRole === "Edit User";

  const systemFilteredData = selectedSystem === "All"
    ? MOCK_USER_ASSIGNMENTS
    : MOCK_USER_ASSIGNMENTS.filter((item) => item.system === selectedSystem);

  const filteredData = systemFilteredData.filter((item) => {
    if (!filterText) return true;
    const lower = filterText.toLowerCase();
    return (
      item.user.toLowerCase().includes(lower) ||
      item.role.toLowerCase().includes(lower) ||
      item.system.toLowerCase().includes(lower) ||
      item.operations.some((o) => o.toLowerCase().includes(lower)) ||
      item.cm.some((c) => c.toLowerCase().includes(lower))
    );
  });

  // For OCL view, filter by system first, then by simulated user's data scope
  const systemFilteredOcl = selectedSystem === "All"
    ? MOCK_OCL_VIEW
    : MOCK_OCL_VIEW.filter((item) => item.system === selectedSystem);

  const scopedOclView = simulatedUser
    ? systemFilteredOcl.filter((item) =>
        (simulatedUser.operations.includes("All") || simulatedUser.operations.includes(item.operation)) &&
        (simulatedUser.cm.includes("All") || simulatedUser.cm.includes(item.cm))
      )
    : systemFilteredOcl;

  const filteredByOcl = scopedOclView.filter((item) => {
    if (!filterText) return true;
    const lower = filterText.toLowerCase();
    return (
      item.operation.toLowerCase().includes(lower) ||
      item.cm.toLowerCase().includes(lower) ||
      item.location.toLowerCase().includes(lower) ||
      item.users.some((u) => u.user.toLowerCase().includes(lower))
    );
  });

  const handleViewModeChange = (mode: DataViewMode) => {
    setViewMode(mode);
    setFilterText("");
    setSelectedItems([]);
    setSelectedOclItems([]);
  };

  const hasSelection = viewMode === "by-user" ? selectedItems.length > 0 : selectedOclItems.length > 0;
  const hasSingleSelection = viewMode === "by-user" ? selectedItems.length === 1 : selectedOclItems.length === 1;

  const getRolePreview = (roleName: string | null) => {
    if (!roleName) return null;
    return MOCK_ROLE_DEFINITIONS.find((r) => r.roleName === roleName);
  };

  const assignRolePreviews = assignRoles.map((r) => getRolePreview(r.value)).filter(Boolean) as RoleDefinition[];
  const editRolePreview = getRolePreview(editRole?.value ?? null);

  const resetAssignForm = () => {
    setAssignSystem(selectedSystem === "All" ? null : { label: selectedSystem, value: selectedSystem });
    setAssignUserType({ label: "Amazon User", value: "amazon-user" });
    setAssignUser("");
    setAssignRoles([]);
    setAssignOperations([]);
    setAssignCm([]);
    setAssignLocations([]);
    setAssignPrograms([]);
    setAssignVisible(false);
  };

  const handleEdit = () => {
    if (viewMode === "by-user" && selectedItems.length === 1) {
      const item = selectedItems[0];
      setEditRole({ label: item.role, value: item.role });
      setEditOperations(item.operations.map((o) => ({ label: o, value: o })));
      setEditCm(item.cm.map((c) => ({ label: c, value: c })));
      setEditLocations(item.locations.map((l) => ({ label: l, value: l })));
      setEditPrograms(item.programs.map((p) => ({ label: p, value: p })));
      setEditVisible(true);
    } else if (viewMode === "by-ocl" && selectedOclItems.length === 1) {
      const item = selectedOclItems[0];
      setEditRole(null);
      setEditOperations([{ label: item.operation, value: item.operation }]);
      setEditCm([{ label: item.cm, value: item.cm }]);
      setEditLocations([{ label: item.location, value: item.location }]);
      setEditPrograms([]);
      setOclAddedUsers([]);
      setEditVisible(true);
    }
  };

  const handleMultiselectAll = (
    selected: readonly any[],
    prev: readonly any[],
    setter: (val: readonly any[]) => void
  ) => {
    const hasAll = selected.some((o) => o.value === "All");
    const prevHadAll = prev.some((o: any) => o.value === "All");
    if (hasAll && !prevHadAll) {
      setter([{ label: "All", value: "All" }]);
    } else if (hasAll && selected.length > 1) {
      setter(selected.filter((o) => o.value !== "All"));
    } else {
      setter(selected);
    }
  };

  return (
    <div className="users-roles-section">
      <SpaceBetween size="l">
        {/* ── Role Reference (collapsible) ── */}
        <ExpandableSection headerText="Role Reference" defaultExpanded={false}>
          <div className="role-summary-bar">
            {selectedSystem === "All" ? (
              ALL_SYSTEMS.map((sys) => (
                <div key={sys.value}>
                  <Box variant="h4" margin={{ top: "xs", bottom: "xxs" }}>{sys.label}</Box>
                  {MOCK_ROLE_DEFINITIONS.filter((r) => r.system === sys.value).map((role) => (
                    <div key={role.id} className={`role-summary-item ${role.status === "inactive" ? "row-inactive" : ""}`}>
                      <Badge color={getRoleBadgeColor(role.roleName)}>{role.roleName}</Badge>
                      <span className="role-summary-desc">{role.description}</span>
                      {role.status === "inactive" && <StatusIndicator type="stopped">Inactive</StatusIndicator>}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              MOCK_ROLE_DEFINITIONS.filter((r) => r.system === selectedSystem).map((role) => (
                <div key={role.id} className={`role-summary-item ${role.status === "inactive" ? "row-inactive" : ""}`}>
                  <Badge color={getRoleBadgeColor(role.roleName)}>{role.roleName}</Badge>
                  <span className="role-summary-desc">{role.description}</span>
                  {role.status === "inactive" && <StatusIndicator type="stopped">Inactive</StatusIndicator>}
                </div>
              ))
            )}
          </div>
        </ExpandableSection>

        {/* ── User Assignments ── */}
        <Container
          header={
            <Header
              variant="h2"
              description={
                viewMode === "by-user"
                  ? "Assign roles and data scope to users. Each row = one user assignment."
                  : "View by Operation/CM/Location: each row shows who has access to a specific scope."
              }
              counter={`(${viewMode === "by-user" ? filteredData.length : filteredByOcl.length})`}
              actions={
                (canManagePermissions || canEditOnly) ? (
                  <SpaceBetween direction="horizontal" size="xs">
                    {(canManagePermissions || canEditOnly) && <Button iconName="add-plus" onClick={() => setAssignVisible(true)}>Assign Role</Button>}
                    <Button disabled={!hasSingleSelection} onClick={handleEdit}>Edit</Button>
                    {canManagePermissions && (
                      <ButtonDropdown
                        items={[
                          { id: "activate", text: "Activate" },
                          { id: "deactivate", text: "Deactivate" },
                          { id: "delete", text: "Delete" },
                        ]}
                        onItemClick={({ detail }) => {
                          if (detail.id === "activate") { setStatusAction("activate"); setStatusVisible(true); }
                          else if (detail.id === "deactivate") { setStatusAction("deactivate"); setStatusVisible(true); }
                          else if (detail.id === "delete") { setDeleteVisible(true); }
                        }}
                        disabled={!hasSelection}
                      >
                        Actions
                      </ButtonDropdown>
                    )}
                  </SpaceBetween>
                ) : undefined
              }
            >
              User Assignments
            </Header>
          }
        >
          <SpaceBetween size="m">
            <div className="toolbar-row">
              <div className="view-mode-toggle">
                <span className="view-mode-label">View:</span>
                <Button
                  variant={viewMode === "by-user" ? "primary" : "normal"}
                  onClick={() => handleViewModeChange("by-user")}
                >
                  By User
                </Button>
                <Button
                  variant={viewMode === "by-ocl" ? "primary" : "normal"}
                  onClick={() => handleViewModeChange("by-ocl")}
                >
                  By Operation / CM / Location
                </Button>
              </div>
            </div>

            {/* ── By User Table ── */}
            {viewMode === "by-user" && (
              <Table
                items={filteredData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)}
                selectedItems={selectedItems}
                onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
                selectionType={(canManagePermissions || canEditOnly) ? "multi" : undefined}
                isItemDisabled={simulatedUser ? (item) => item.assignedBy !== simulatedUser.user : undefined}
                trackBy="id"
                columnDefinitions={[
                  {
                    id: "user",
                    header: "User",
                    cell: (item) => (
                      <span className={item.status === "inactive" ? "row-inactive" : ""}>
                        <Box fontWeight="bold">{item.user}</Box>
                      </span>
                    ),
                    sortingField: "user",
                    width: 140,
                  },
                  ...(selectedSystem === "All" ? [{
                    id: "system",
                    header: "System",
                    cell: (item: UserAssignment) => (
                      <span className={item.status === "inactive" ? "row-inactive" : ""}>
                        <Badge color={item.system === "MSA" ? "blue" : item.system === "Atlas" ? "green" : "grey"}>{item.system}</Badge>
                      </span>
                    ),
                    sortingField: "system",
                    width: 110,
                  }] : []),
                  {
                    id: "dataScope",
                    header: "Data Scope",
                    cell: (item) => (
                      <span className={item.status === "inactive" ? "row-inactive" : ""}>
                        <div className="data-scope-cell">
                          <div className="data-scope-row">
                            <span className="data-scope-label">Operation:</span>
                            {item.operations.map((o, i) => (
                              <span key={i} className={`permission-value-tag ${o === "All" ? "tag-all" : ""}`}>{o}</span>
                            ))}
                          </div>
                          <div className="data-scope-row">
                            <span className="data-scope-label">CM:</span>
                            {item.cm.map((c, i) => (
                              <span key={i} className={`permission-value-tag ${c === "All" ? "tag-all" : ""}`}>{c}</span>
                            ))}
                          </div>
                          <div className="data-scope-row">
                            <span className="data-scope-label">Location:</span>
                            {item.locations.map((l, i) => (
                              <span key={i} className={`permission-value-tag ${l === "All" ? "tag-all" : ""}`}>{l}</span>
                            ))}
                          </div>
                          <div className="data-scope-row">
                            <span className="data-scope-label">Program:</span>
                            {item.programs.map((p, i) => (
                              <span key={i} className={`permission-value-tag ${p === "All" ? "tag-all" : ""}`}>{p}</span>
                            ))}
                          </div>
                        </div>
                      </span>
                    ),
                    width: 320,
                  },
                  {
                    id: "role",
                    header: "Role",
                    cell: (item) => (
                      <span className={item.status === "inactive" ? "row-inactive" : ""}>
                        <Badge color={getRoleBadgeColor(item.role)}>{item.role}</Badge>
                      </span>
                    ),
                    width: 140,
                  },
                  {
                    id: "assignedBy",
                    header: "Assigned By",
                    cell: (item) => (
                      <span className={item.status === "inactive" ? "row-inactive" : ""}>{item.assignedBy}</span>
                    ),
                    width: 120,
                  },
                  {
                    id: "status",
                    header: "Status",
                    cell: (item) => (
                      <StatusIndicator type={item.status === "active" ? "success" : "stopped"}>
                        {item.status === "active" ? "Active" : "Inactive"}
                      </StatusIndicator>
                    ),
                    width: 100,
                  },
                ]}
                filter={
                  <TextFilter
                    filteringText={filterText}
                    filteringPlaceholder="Search by user, role, operation, or CM..."
                    onChange={({ detail }) => { setFilterText(detail.filteringText); setCurrentPage(1); }}
                  />
                }
                pagination={
                  <Pagination
                    currentPageIndex={currentPage}
                    pagesCount={Math.ceil(filteredData.length / PAGE_SIZE)}
                    onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                  />
                }
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No user assignments</b>
                    <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                      No assignments match the current filters.
                    </Box>
                  </Box>
                }
              />
            )}

            {/* ── By Operation/CM/Location Table ── */}
            {viewMode === "by-ocl" && (
              <SpaceBetween size="s">
              <div className="role-legend">
                {(selectedSystem === "All" ? MOCK_ROLE_DEFINITIONS : MOCK_ROLE_DEFINITIONS.filter((r) => r.system === selectedSystem))
                  .filter((r, i, arr) => arr.findIndex((x) => x.roleName === r.roleName) === i)
                  .map((r) => (
                  <span key={r.id} className="role-legend-item">
                    <Badge color={getRoleBadgeColor(r.roleName)}>{getRoleAbbreviation(r.roleName)}</Badge>
                    <span className="role-legend-label">= {r.roleName}</span>
                  </span>
                ))}
              </div>
              <Table
                items={filteredByOcl.slice((oclCurrentPage - 1) * PAGE_SIZE, oclCurrentPage * PAGE_SIZE)}
                selectedItems={selectedOclItems}
                onSelectionChange={({ detail }) => setSelectedOclItems(detail.selectedItems)}
                selectionType={(canManagePermissions || canEditOnly) ? "multi" : undefined}
                isItemDisabled={simulatedUser ? (item) => {
                  const userInLine = item.users.find((u) => u.user === simulatedUser.user);
                  return !userInLine || userInLine.role !== "Administrator";
                } : undefined}
                trackBy="id"
                columnDefinitions={[
                  ...(selectedSystem === "All" ? [{
                    id: "system",
                    header: "System",
                    cell: (item: OclViewItem) => (
                      <span className={item.status === "inactive" ? "row-inactive" : ""}>
                        <Badge color={item.system === "MSA" ? "blue" : item.system === "Atlas" ? "green" : "grey"}>{item.system}</Badge>
                      </span>
                    ),
                    sortingField: "system",
                    width: 110,
                  }] : []),
                  {
                    id: "operation",
                    header: "Operation",
                    cell: (item) => (
                      <span className={item.status === "inactive" ? "row-inactive" : ""}>
                        <Box fontWeight="bold">{item.operation}</Box>
                      </span>
                    ),
                    sortingField: "operation",
                    width: 120,
                  },
                  {
                    id: "cm",
                    header: "CM",
                    cell: (item) => (
                      <span className={item.status === "inactive" ? "row-inactive" : ""}>
                        <Box fontWeight="bold">{item.cm}</Box>
                      </span>
                    ),
                    sortingField: "cm",
                    width: 120,
                  },
                  {
                    id: "location",
                    header: "Location",
                    cell: (item) => (
                      <span className={item.status === "inactive" ? "row-inactive" : ""}>
                        <span className={`permission-value-tag ${item.location === "All Locations" ? "tag-all" : ""}`}>
                          {item.location}
                        </span>
                      </span>
                    ),
                    width: 140,
                  },
                  {
                    id: "users",
                    header: "Authorized Users",
                    cell: (item) => (
                      <div className={`cm-view-users ${item.status === "inactive" ? "row-inactive" : ""}`}>
                        {item.users.map((u, i) => (
                          <span key={i} className="cm-view-user-chip">
                            <span className="cm-view-user-name">{u.user}</span>
                            <Badge color={getRoleBadgeColor(u.role)}>{getRoleAbbreviation(u.role)}</Badge>
                            {u.programs[0] !== "All" && (
                              <span className="cm-view-user-programs">
                                ({u.programs.join(", ")})
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    ),
                    width: 450,
                  },
                  {
                    id: "status",
                    header: "Status",
                    cell: (item) => (
                      <StatusIndicator type={item.status === "active" ? "success" : "stopped"}>
                        {item.status === "active" ? "Active" : "Inactive"}
                      </StatusIndicator>
                    ),
                    width: 100,
                  },
                ]}
                filter={
                  <TextFilter
                    filteringText={filterText}
                    filteringPlaceholder="Search by operation, CM, location, or user..."
                    onChange={({ detail }) => { setFilterText(detail.filteringText); setOclCurrentPage(1); }}
                  />
                }
                pagination={
                  <Pagination
                    currentPageIndex={oclCurrentPage}
                    pagesCount={Math.ceil(filteredByOcl.length / PAGE_SIZE)}
                    onChange={({ detail }) => setOclCurrentPage(detail.currentPageIndex)}
                  />
                }
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No data found</b>
                    <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                      No results match the current filters.
                    </Box>
                  </Box>
                }
              />
              </SpaceBetween>
            )}
          </SpaceBetween>
        </Container>
      </SpaceBetween>

      {/* ── Assign Role Modal ── */}
      <Modal
        visible={assignVisible}
        onDismiss={resetAssignForm}
        header="Assign Role"
        size="large"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={resetAssignForm}>Cancel</Button>
              <Button variant="primary" disabled={!assignUser.trim() || assignRoles.length === 0 || assignOperations.length === 0 || assignCm.length === 0}>
                Assign
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          {selectedSystem === "All" && (
            <FormField
              label={<span>System <span className="required-asterisk">*</span></span>}
              description="Select the system this assignment applies to."
            >
              <Select
                selectedOption={assignSystem}
                onChange={({ detail }) => setAssignSystem(detail.selectedOption)}
                options={ALL_SYSTEMS}
                placeholder="Select system"
              />
            </FormField>
          )}

          {viewMode === "by-user" && (
            <FormField
              label={<span>User / Team / Vendor <span className="required-asterisk">*</span></span>}
              description="Select a type and enter a user login, team name, or vendor identifier."
            >
              <div style={{ display: "flex", gap: "8px" }}>
                <div style={{ width: "180px", flexShrink: 0 }}>
                  <Select
                    selectedOption={assignUserType}
                    onChange={({ detail }) => setAssignUserType(detail.selectedOption)}
                    options={[
                      { label: "Amazon User", value: "amazon-user" },
                      { label: "Team", value: "team" },
                      { label: "Vendor", value: "vendor" },
                    ]}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Input
                    value={assignUser}
                    onChange={({ detail }) => setAssignUser(detail.value)}
                    placeholder={
                      assignUserType?.value === "team" ? "e.g. msa-core-team" :
                      assignUserType?.value === "vendor" ? "e.g. vendor-foxconn-ops" :
                      "e.g. john.doe"
                    }
                  />
                </div>
              </div>
            </FormField>
          )}

          <FormField
            label={<span>Role <span className="required-asterisk">*</span></span>}
            description={`Determines feature access. You can assign roles below your level (current: ${simulatedRole || CURRENT_USER_ROLE}).`}
          >
            <Multiselect
              selectedOptions={assignRoles}
              onChange={({ detail }) => setAssignRoles(detail.selectedOptions)}
              options={getAssignableRoles(simulatedRole || CURRENT_USER_ROLE)}
              placeholder="Select role(s)"
              tokenLimit={3}
            />
          </FormField>

          {assignRolePreviews.length > 0 && (
            <div className="role-preview-box">
              <Box fontSize="body-s" color="text-body-secondary" margin={{ bottom: "xxs" }}>Selected roles can access:</Box>
              <div className="permission-tags-wrap">
                {assignRolePreviews.every((r) => r.permissions.every((p) => p.actions.every((a) => a.allowed)))
                  ? <span className="permission-value-tag tag-all">All Features, All Actions</span>
                  : assignRolePreviews.map((r) => (
                      <div key={r.id} style={{ marginBottom: 4 }}>
                        <Badge color={getRoleBadgeColor(r.roleName)}>{r.roleName}</Badge>{" "}
                        {r.permissions
                          .filter((p) => p.actions.some((a) => a.allowed))
                          .map((p) => (
                            <span key={p.feature} className="permission-tag">
                              {p.feature}: {p.actions.filter((a) => a.allowed).map((a) => a.action).join(", ")}
                            </span>
                          ))
                        }
                      </div>
                    ))
                }
              </div>
            </div>
          )}

          <Box variant="h4" margin={{ top: "xs" }}>Data Scope</Box>

          <FormField label={<span>Operation <span className="required-asterisk">*</span></span>}>
            <Multiselect
              selectedOptions={assignOperations}
              onChange={({ detail }) => handleMultiselectAll(detail.selectedOptions, assignOperations, setAssignOperations)}
              options={ALL_OPERATIONS}
              placeholder="Select operation(s)"
              tokenLimit={3}
            />
          </FormField>

          <FormField label={<span>CM <span className="required-asterisk">*</span></span>}>
            <Multiselect
              selectedOptions={assignCm}
              onChange={({ detail }) => handleMultiselectAll(detail.selectedOptions, assignCm, setAssignCm)}
              options={ALL_CMS}
              placeholder="Select CM(s)"
              tokenLimit={3}
            />
          </FormField>

          <FormField label="Location">
            <Multiselect
              selectedOptions={assignLocations}
              onChange={({ detail }) => handleMultiselectAll(detail.selectedOptions, assignLocations, setAssignLocations)}
              options={ALL_LOCATIONS}
              placeholder="Select location(s) — defaults to All"
              tokenLimit={3}
            />
          </FormField>

          <FormField label="Program">
            <Multiselect
              selectedOptions={assignPrograms}
              onChange={({ detail }) => handleMultiselectAll(detail.selectedOptions, assignPrograms, setAssignPrograms)}
              options={ALL_PROGRAMS}
              placeholder="Select program(s) — defaults to All"
              tokenLimit={3}
            />
          </FormField>

          {viewMode === "by-ocl" && (
            <FormField
              label={<span>User / Team / Vendor <span className="required-asterisk">*</span></span>}
              description="Select a type and enter a user login, team name, or vendor identifier."
            >
              <div style={{ display: "flex", gap: "8px" }}>
                <div style={{ width: "180px", flexShrink: 0 }}>
                  <Select
                    selectedOption={assignUserType}
                    onChange={({ detail }) => setAssignUserType(detail.selectedOption)}
                    options={[
                      { label: "Amazon User", value: "amazon-user" },
                      { label: "Team", value: "team" },
                      { label: "Vendor", value: "vendor" },
                    ]}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Input
                    value={assignUser}
                    onChange={({ detail }) => setAssignUser(detail.value)}
                    placeholder={
                      assignUserType?.value === "team" ? "e.g. msa-core-team" :
                      assignUserType?.value === "vendor" ? "e.g. vendor-foxconn-ops" :
                      "e.g. john.doe"
                    }
                  />
                </div>
              </div>
            </FormField>
          )}
        </SpaceBetween>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal
        visible={editVisible}
        onDismiss={() => setEditVisible(false)}
        header={viewMode === "by-user" ? `Edit — ${selectedItems[0]?.user ?? ""}` : `Edit — ${selectedOclItems[0]?.operation ?? ""} / ${selectedOclItems[0]?.cm ?? ""} / ${selectedOclItems[0]?.location ?? ""}`}
        size="large"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setEditVisible(false)}>Cancel</Button>
              <Button variant="primary">Save</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          {viewMode === "by-user" && (
            <FormField label="User">
              <Input value={selectedItems[0]?.user ?? ""} disabled />
            </FormField>
          )}

          <Box variant="h4" margin={{ top: "xs" }}>Data Scope</Box>

          <FormField label="Operation">
            <Multiselect
              selectedOptions={editOperations}
              onChange={({ detail }) => handleMultiselectAll(detail.selectedOptions, editOperations, setEditOperations)}
              options={ALL_OPERATIONS}
              placeholder="Select operation(s)"
              tokenLimit={3}
              disabled={viewMode === "by-ocl"}
            />
          </FormField>

          <FormField label="CM">
            <Multiselect
              selectedOptions={editCm}
              onChange={({ detail }) => handleMultiselectAll(detail.selectedOptions, editCm, setEditCm)}
              options={ALL_CMS}
              placeholder="Select CM(s)"
              tokenLimit={3}
              disabled={viewMode === "by-ocl"}
            />
          </FormField>

          <FormField label="Location">
            <Multiselect
              selectedOptions={editLocations}
              onChange={({ detail }) => handleMultiselectAll(detail.selectedOptions, editLocations, setEditLocations)}
              options={ALL_LOCATIONS}
              placeholder="Select location(s)"
              tokenLimit={3}
              disabled={viewMode === "by-ocl"}
            />
          </FormField>

          <FormField label="Program">
            <Multiselect
              selectedOptions={editPrograms}
              onChange={({ detail }) => handleMultiselectAll(detail.selectedOptions, editPrograms, setEditPrograms)}
              options={ALL_PROGRAMS}
              placeholder="Select program(s)"
              tokenLimit={3}
            />
          </FormField>

          {viewMode === "by-ocl" && selectedOclItems.length === 1 && (
            <FormField label="Users on this line">
              <div className="edit-ocl-users-list">
                {selectedOclItems[0].users.map((u) => {
                  const currentRole = simulatedRole || CURRENT_USER_ROLE;
                  const isProtected = u.role === "System Admin" || (u.role === "Administrator" && currentRole !== "System Admin");
                  return (
                    <div key={u.user} className={`edit-ocl-user-row ${isProtected ? "disabled" : ""}`}>
                      <span className="edit-ocl-user-name">{u.user}</span>
                      {isProtected ? (
                        <>
                          <Badge color={getRoleBadgeColor(u.role)}>{u.role}</Badge>
                          <span className="edit-ocl-user-hint">cannot modify</span>
                        </>
                      ) : (
                        <>
                          <Select
                            selectedOption={{ label: u.role, value: u.role }}
                            onChange={() => {}}
                            options={getAssignableRoles(simulatedRole || CURRENT_USER_ROLE)}
                            placeholder="Role"
                          />
                          <Button variant="link">Delete</Button>
                        </>
                      )}
                    </div>
                  );
                })}
                {oclAddedUsers.map((added, idx) => (
                  <div key={`added-${idx}`} className="edit-ocl-user-row">
                    <span className="edit-ocl-user-name">{added.user}</span>
                    <Badge color={getRoleBadgeColor(added.role as RoleName)}>{added.role}</Badge>
                    <Button variant="link" onClick={() => setOclAddedUsers(oclAddedUsers.filter((_, i) => i !== idx))}>Remove</Button>
                  </div>
                ))}
                <div className="edit-ocl-add-user">
                  <Input
                    value={oclAddUserName}
                    onChange={({ detail }) => setOclAddUserName(detail.value)}
                    placeholder="Add user login or team name"
                  />
                  <Select
                    selectedOption={oclAddUserRole}
                    onChange={({ detail }) => setOclAddUserRole(detail.selectedOption)}
                    options={getAssignableRoles(simulatedRole || CURRENT_USER_ROLE)}
                    placeholder="Role"
                  />
                  <Button variant="normal" disabled={!oclAddUserName.trim() || !oclAddUserRole} onClick={() => {
                    setOclAddedUsers([...oclAddedUsers, { user: oclAddUserName.trim(), role: oclAddUserRole.value }]);
                    setOclAddUserName("");
                    setOclAddUserRole(null);
                  }}>Add</Button>
                </div>
              </div>
            </FormField>
          )}

          {viewMode === "by-user" && (
            <>
              <Box variant="h4" margin={{ top: "xs" }}>Role</Box>
              <FormField label="Role">
                <Select
                  selectedOption={editRole}
                  onChange={({ detail }) => setEditRole(detail.selectedOption)}
                  options={getAssignableRoles(simulatedRole || CURRENT_USER_ROLE)}
                  placeholder="Select a role"
                />
              </FormField>

              {editRolePreview && (
                <div className="role-preview-box">
                  <Box variant="h4" margin={{ bottom: "xs" }}>This role can access:</Box>
                  <div className="permission-tags-wrap">
                    {editRolePreview.permissions.every((p) => p.actions.every((a) => a.allowed))
                      ? <span className="permission-value-tag tag-all">All Features, All Actions</span>
                      : editRolePreview.permissions
                          .filter((p) => p.actions.some((a) => a.allowed))
                          .map((p) => (
                            <span key={p.feature} className="permission-tag">
                              {p.feature}: {p.actions.filter((a) => a.allowed).map((a) => a.action).join(", ")}
                            </span>
                          ))
                    }
                  </div>
                </div>
              )}
            </>
          )}
        </SpaceBetween>
      </Modal>

      {/* ── Delete Modal ── */}
      <Modal
        visible={deleteVisible}
        onDismiss={() => setDeleteVisible(false)}
        header="Delete Assignment"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDeleteVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => { setDeleteVisible(false); setSelectedItems([]); setSelectedOclItems([]); }}>Delete</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box variant="p">
          Are you sure you want to delete {viewMode === "by-user" ? selectedItems.length : selectedOclItems.length} assignment{(viewMode === "by-user" ? selectedItems.length : selectedOclItems.length) > 1 ? "s" : ""}?
        </Box>
        <Box variant="p" color="text-body-secondary" margin={{ top: "s" }}>
          {viewMode === "by-user"
            ? selectedItems.map((item) => `${item.user} (${item.role})`).join(", ")
            : selectedOclItems.map((item) => `${item.operation} / ${item.cm} / ${item.location}`).join(", ")
          }
        </Box>
      </Modal>

      {/* ── Status Change Modal ── */}
      <Modal
        visible={statusVisible}
        onDismiss={() => setStatusVisible(false)}
        header={`Confirm ${statusAction === "activate" ? "Activate" : "Deactivate"}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setStatusVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => { setStatusVisible(false); setSelectedItems([]); setSelectedOclItems([]); }}>Confirm</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box variant="p">
          Are you sure you want to {statusAction} {viewMode === "by-user" ? selectedItems.length : selectedOclItems.length} assignment{(viewMode === "by-user" ? selectedItems.length : selectedOclItems.length) > 1 ? "s" : ""}?
        </Box>
        <Box variant="p" color="text-body-secondary" margin={{ top: "s" }}>
          {viewMode === "by-user"
            ? selectedItems.map((item) => `${item.user} (${item.role})`).join(", ")
            : selectedOclItems.map((item) => `${item.operation} / ${item.cm} / ${item.location}`).join(", ")
          }
        </Box>
      </Modal>
    </div>
  );
};

// ─── Default permissions template for new roles ─────────────────────────────
// ─── System-specific feature definitions ────────────────────────────────────
const SYSTEM_FEATURES: Record<SystemName, FeaturePermission[]> = {
  MSA: [
    { feature: "Executive Overview", actions: [{ action: "View", allowed: false }] },
    { feature: "Monitor & Alert", actions: [{ action: "View", allowed: false }, { action: "Configure", allowed: false }] },
    { feature: "Intelligent Analysis", actions: [{ action: "View", allowed: false }, { action: "Run", allowed: false }] },
    { feature: "Report Agent", actions: [{ action: "View", allowed: false }, { action: "Generate", allowed: false }] },
    { feature: "Process Optimization", actions: [{ action: "View", allowed: false }, { action: "Execute", allowed: false }] },
    { feature: "Configuration Setting", actions: [{ action: "View List", allowed: false }, { action: "Onboard", allowed: false }, { action: "Update", allowed: false }, { action: "Enable/Disable", allowed: false }] },
    { feature: "Permission Management", actions: [{ action: "View", allowed: false }, { action: "Manage", allowed: false }] },
  ],
  Atlas: [
    { feature: "Dashboard", actions: [{ action: "View", allowed: false }] },
    { feature: "Data Pipeline", actions: [{ action: "View", allowed: false }, { action: "Run", allowed: false }, { action: "Configure", allowed: false }] },
    { feature: "Query Editor", actions: [{ action: "View", allowed: false }, { action: "Execute", allowed: false }] },
    { feature: "Scheduling", actions: [{ action: "View", allowed: false }, { action: "Create", allowed: false }, { action: "Edit", allowed: false }] },
    { feature: "Permission Management", actions: [{ action: "View", allowed: false }, { action: "Manage", allowed: false }] },
  ],
  Lighthouse: [
    { feature: "Health Monitor", actions: [{ action: "View", allowed: false }, { action: "Configure", allowed: false }] },
    { feature: "Incident Management", actions: [{ action: "View", allowed: false }, { action: "Create", allowed: false }, { action: "Resolve", allowed: false }] },
    { feature: "Runbook", actions: [{ action: "View", allowed: false }, { action: "Execute", allowed: false }, { action: "Edit", allowed: false }] },
    { feature: "Permission Management", actions: [{ action: "View", allowed: false }, { action: "Manage", allowed: false }] },
  ],
};

const getDefaultPermissions = (system: string): FeaturePermission[] => {
  if (system && system !== "All" && SYSTEM_FEATURES[system as SystemName]) {
    return SYSTEM_FEATURES[system as SystemName].map((p) => ({ ...p, actions: p.actions.map((a) => ({ ...a })) }));
  }
  return SYSTEM_FEATURES.MSA.map((p) => ({ ...p, actions: p.actions.map((a) => ({ ...a })) }));
};

// ─── Role Definitions Tab ───────────────────────────────────────────────────
const RoleDefinitionsTab: React.FC<{ canEdit: boolean; selectedSystem: string }> = ({ canEdit, selectedSystem }) => {
  const [selectedRoles, setSelectedRoles] = useState<RoleDefinition[]>([]);
  const [roleCurrentPage, setRoleCurrentPage] = useState(1);
  const [roleFilterText, setRoleFilterText] = useState("");

  // Add Role Modal
  const [addRoleVisible, setAddRoleVisible] = useState(false);
  const [newRoleSystem, setNewRoleSystem] = useState<any>(
    selectedSystem === "All" ? null : { label: selectedSystem, value: selectedSystem }
  );
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDescription, setNewRoleDescription] = useState("");
  const [newRolePermissions, setNewRolePermissions] = useState<FeaturePermission[]>(
    getDefaultPermissions(selectedSystem)
  );

  // Edit Role Definition Modal
  const [editRoleDefVisible, setEditRoleDefVisible] = useState(false);
  const [editingRoleDef, setEditingRoleDef] = useState<RoleDefinition | null>(null);
  const [editRolePermissions, setEditRolePermissions] = useState<FeaturePermission[]>([]);

  // Delete Role Modal
  const [deleteRoleVisible, setDeleteRoleVisible] = useState(false);

  const handleEditRoleDef = () => {
    if (selectedRoles.length === 1) {
      const role = selectedRoles[0];
      setEditingRoleDef(role);
      setEditRolePermissions([...role.permissions]);
      setEditRoleDefVisible(true);
    }
  };

  const resetAddRoleForm = () => {
    setNewRoleSystem(selectedSystem === "All" ? null : { label: selectedSystem, value: selectedSystem });
    setNewRoleName("");
    setNewRoleDescription("");
    setNewRolePermissions(getDefaultPermissions(selectedSystem));
    setAddRoleVisible(false);
  };

  const hasRoleSelection = selectedRoles.length > 0;

  return (
    <div className="role-definitions-section">
      <SpaceBetween size="l">
        <Container
          header={
            <Header
              variant="h2"
              description="Define roles and their feature access permissions. Role determines what actions a user can perform."
              counter={`(${MOCK_ROLE_DEFINITIONS.length})`}
              actions={
                canEdit ? (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button iconName="add-plus" onClick={() => setAddRoleVisible(true)}>Add Role</Button>
                    <ButtonDropdown
                      items={[
                        { id: "edit", text: "Edit", disabled: selectedRoles.length !== 1 },
                        { id: "delete", text: "Delete", disabled: !hasRoleSelection },
                        { id: "inactive", text: "Inactive", disabled: !hasRoleSelection },
                        { id: "active", text: "Active", disabled: !hasRoleSelection },
                      ]}
                      onItemClick={({ detail }) => {
                        if (detail.id === "edit") { handleEditRoleDef(); }
                        else if (detail.id === "delete") { setDeleteRoleVisible(true); }
                      }}
                      disabled={!hasRoleSelection}
                    >
                      Actions
                    </ButtonDropdown>
                  </SpaceBetween>
                ) : undefined
              }
            >
              Roles
            </Header>
          }
        >
          <Table
            items={(() => {
              const systemFiltered = selectedSystem === "All" ? MOCK_ROLE_DEFINITIONS : MOCK_ROLE_DEFINITIONS.filter((r) => r.system === selectedSystem);
              const filtered = roleFilterText
                ? systemFiltered.filter((r) => {
                    const lower = roleFilterText.toLowerCase();
                    return r.roleName.toLowerCase().includes(lower) || r.description.toLowerCase().includes(lower) || r.type.toLowerCase().includes(lower) || r.system.toLowerCase().includes(lower);
                  })
                : systemFiltered;
              return filtered.slice((roleCurrentPage - 1) * PAGE_SIZE, roleCurrentPage * PAGE_SIZE);
            })()}
            selectedItems={selectedRoles}
            onSelectionChange={({ detail }) => setSelectedRoles(detail.selectedItems)}
            selectionType={canEdit ? "multi" : undefined}
            isItemDisabled={canEdit ? (item) => item.type === "system" : undefined}
            trackBy="id"
            columnDefinitions={[
              ...(selectedSystem === "All" ? [{
                id: "system",
                header: "System",
                cell: (item: RoleDefinition) => (
                  <Badge color={item.system === "MSA" ? "blue" : item.system === "Atlas" ? "green" : "grey"}>
                    {item.system}
                  </Badge>
                ),
                width: 110,
              }] : []),
              {
                id: "roleName",
                header: "Role",
                cell: (item) => (
                  <span className={item.status === "inactive" ? "row-inactive" : ""}>
                    <Badge color={getRoleBadgeColor(item.roleName)}>{item.roleName}</Badge>
                  </span>
                ),
                width: 180,
              },
              {
                id: "type",
                header: "Type",
                cell: (item) => (
                  <span className={`role-type-tag ${item.type} ${item.status === "inactive" ? "row-inactive" : ""}`}>
                    {item.type === "system" ? "System" : "Custom"}
                  </span>
                ),
                width: 90,
              },
              {
                id: "permissions",
                header: "Features & Actions",
                cell: (item) => {
                  const allAllowed = item.permissions.every((perm) => perm.actions.every((a) => a.allowed));
                  if (allAllowed) {
                    return <span className={`permission-value-tag tag-all ${item.status === "inactive" ? "row-inactive" : ""}`}>All Features, All Actions</span>;
                  }
                  return (
                    <div className={`permission-features-grid ${item.status === "inactive" ? "row-inactive" : ""}`}>
                      {item.permissions
                        .filter((perm) => perm.actions.some((a) => a.allowed))
                        .map((perm) => (
                          <div key={perm.feature} className="permission-feature-row">
                            <span className="permission-feature-name">{perm.feature}</span>
                            <span className="permission-actions-cell">
                              {perm.actions
                                .filter((a) => a.allowed)
                                .map((a) => (
                                  <span key={a.action} className="permission-action-tag">{a.action}</span>
                                ))}
                            </span>
                          </div>
                        ))}
                    </div>
                  );
                },
                width: 480,
              },
              {
                id: "description",
                header: "Description",
                cell: (item) => (
                  <span className={item.status === "inactive" ? "row-inactive" : ""}>
                    <Box color="text-body-secondary" fontSize="body-s">{item.description}</Box>
                  </span>
                ),
                width: 160,
              },
              {
                id: "status",
                header: "Status",
                cell: (item) => (
                  <StatusIndicator type={item.status === "active" ? "success" : "stopped"}>
                    {item.status === "active" ? "Active" : "Inactive"}
                  </StatusIndicator>
                ),
                width: 100,
              },
            ]}
            filter={
              <TextFilter
                filteringText={roleFilterText}
                filteringPlaceholder="Search by role name, type, or description..."
                onChange={({ detail }) => { setRoleFilterText(detail.filteringText); setRoleCurrentPage(1); }}
              />
            }
            pagination={
              <Pagination
                currentPageIndex={roleCurrentPage}
                pagesCount={Math.ceil((() => {
                  const systemFiltered = selectedSystem === "All" ? MOCK_ROLE_DEFINITIONS : MOCK_ROLE_DEFINITIONS.filter((r) => r.system === selectedSystem);
                  return roleFilterText ? systemFiltered.filter((r) => {
                    const lower = roleFilterText.toLowerCase();
                    return r.roleName.toLowerCase().includes(lower) || r.description.toLowerCase().includes(lower) || r.type.toLowerCase().includes(lower) || r.system.toLowerCase().includes(lower);
                  }).length : systemFiltered.length;
                })() / PAGE_SIZE)}
                onChange={({ detail }) => setRoleCurrentPage(detail.currentPageIndex)}
              />
            }
            empty={
              <Box textAlign="center" color="inherit">
                <b>No roles defined</b>
              </Box>
            }
          />
        </Container>
      </SpaceBetween>

      {/* ── Add Role Modal ── */}
      <Modal
        visible={addRoleVisible}
        onDismiss={resetAddRoleForm}
        header="Add Role"
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={resetAddRoleForm}>Cancel</Button>
              <Button variant="primary" disabled={!newRoleName.trim()}>Create</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          {selectedSystem === "All" && (
            <FormField label={<span>System <span className="required-asterisk">*</span></span>}>
              <Select
                selectedOption={newRoleSystem}
                onChange={({ detail }) => { setNewRoleSystem(detail.selectedOption); setNewRolePermissions(getDefaultPermissions(detail.selectedOption.value || "MSA")); }}
                options={ALL_SYSTEMS}
                placeholder="Select system"
              />
            </FormField>
          )}
          <FormField label={<span>Role Name <span className="required-asterisk">*</span></span>}>
            <Input
              value={newRoleName}
              onChange={({ detail }) => setNewRoleName(detail.value)}
              placeholder="e.g. Update Program"
            />
          </FormField>
          <FormField label="Description">
            <Input
              value={newRoleDescription}
              onChange={({ detail }) => setNewRoleDescription(detail.value)}
              placeholder="Brief description of this role's purpose"
            />
          </FormField>
          <FormField label="Features & Actions">
            <div className="perm-chip-panel">
              <div
                className={`perm-chip-all ${newRolePermissions.every((p) => p.actions.every((a) => a.allowed)) ? "active" : ""}`}
                onClick={() => {
                  const allSelected = newRolePermissions.every((p) => p.actions.every((a) => a.allowed));
                  setNewRolePermissions(newRolePermissions.map((p) => ({
                    ...p, actions: p.actions.map((a) => ({ ...a, allowed: !allSelected })),
                  })));
                }}
              >
                All Features, All Actions
              </div>
              {newRolePermissions.map((perm, featureIdx) => (
                <div key={perm.feature} className="perm-chip-row">
                  <span className="perm-chip-feature">{perm.feature}</span>
                  <div className="perm-chip-actions">
                    {perm.actions.map((act, actIdx) => (
                      <span
                        key={act.action}
                        className={`perm-chip ${act.allowed ? "active" : ""}`}
                        onClick={() => {
                          const updated = [...newRolePermissions];
                          const updatedActions = [...updated[featureIdx].actions];
                          updatedActions[actIdx] = { ...act, allowed: !act.allowed };
                          updated[featureIdx] = { ...perm, actions: updatedActions };
                          setNewRolePermissions(updated);
                        }}
                      >
                        {act.action}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </FormField>
        </SpaceBetween>
      </Modal>

      {/* ── Edit Role Definition Modal ── */}
      <Modal
        visible={editRoleDefVisible}
        onDismiss={() => setEditRoleDefVisible(false)}
        header={`Edit Role — ${editingRoleDef?.roleName ?? ""}`}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setEditRoleDefVisible(false)}>Cancel</Button>
              <Button variant="primary">Save</Button>
            </SpaceBetween>
          </Box>
        }
      >
        {editingRoleDef && (
          <SpaceBetween size="l">
            <FormField label="Role Name">
              <Input value={editingRoleDef.roleName} disabled />
            </FormField>
            <FormField label="Description">
              <Input value={editingRoleDef.description} onChange={() => {}} />
            </FormField>
            <FormField label="Features & Actions">
              <div className="perm-chip-panel">
                <div
                  className={`perm-chip-all ${editRolePermissions.every((p) => p.actions.every((a) => a.allowed)) ? "active" : ""}`}
                  onClick={() => {
                    const allSelected = editRolePermissions.every((p) => p.actions.every((a) => a.allowed));
                    setEditRolePermissions(editRolePermissions.map((p) => ({
                      ...p, actions: p.actions.map((a) => ({ ...a, allowed: !allSelected })),
                    })));
                  }}
                >
                  All Features, All Actions
                </div>
                {editRolePermissions.map((perm, featureIdx) => (
                  <div key={perm.feature} className="perm-chip-row">
                    <span className="perm-chip-feature">{perm.feature}</span>
                    <div className="perm-chip-actions">
                      {perm.actions.map((act, actIdx) => (
                        <span
                          key={act.action}
                          className={`perm-chip ${act.allowed ? "active" : ""}`}
                          onClick={() => {
                            const updated = [...editRolePermissions];
                            const updatedActions = [...updated[featureIdx].actions];
                            updatedActions[actIdx] = { ...act, allowed: !act.allowed };
                            updated[featureIdx] = { ...perm, actions: updatedActions };
                            setEditRolePermissions(updated);
                          }}
                        >
                          {act.action}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </FormField>
          </SpaceBetween>
        )}
      </Modal>

      {/* ── Delete Role Modal ── */}
      <Modal
        visible={deleteRoleVisible}
        onDismiss={() => setDeleteRoleVisible(false)}
        header="Delete Role"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDeleteRoleVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => { setDeleteRoleVisible(false); setSelectedRoles([]); }}>Delete</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Box variant="p">
          Are you sure you want to delete the role <strong>{selectedRoles[0]?.roleName}</strong>? Users assigned this role will lose their permissions.
        </Box>
      </Modal>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────
const PermissionManagement: React.FC = () => {
  const [activeTabId, setActiveTabId] = useState("users-roles");
  const [globalSelectedSystem, setGlobalSelectedSystem] = useState<any>({ label: "MSA", value: "MSA" });

  const isSystemAdmin = CURRENT_USER_ROLE === "System Admin";
  const effectiveSystem = isSystemAdmin ? (globalSelectedSystem?.value || "MSA") : CURRENT_SYSTEM;

  const canSeeRoleDefinitions =
    CURRENT_USER_ROLE === "System Admin" ||
    CURRENT_USER_ROLE === "Administrator" ||
    CURRENT_USER_ROLE === "Viewer";

  const canEditRoleDefinitions =
    CURRENT_USER_ROLE === "System Admin" ||
    CURRENT_USER_ROLE === "Administrator";

  const tabs = [
    {
      id: "users-roles",
      label: "Users & Roles",
      content: <UsersAndRolesTab simulatedRole={null} simulatedUser={null} selectedSystem={effectiveSystem} />,
    },
    ...(canSeeRoleDefinitions ? [{
      id: "role-definitions",
      label: "Role Definitions",
      content: <RoleDefinitionsTab canEdit={canEditRoleDefinitions} selectedSystem={effectiveSystem} />,
    }] : []),
  ];

  return (
    <div className="permission-management" data-testid="permission-management-page">
      <div className="permission-header">
        <div className="permission-header-row">
          <Header
            variant="h1"
            description="Users & Roles: assign roles and data scope to users. Role Definitions: define roles and configure feature-level permissions."
          >
            Permission Management
          </Header>
          {isSystemAdmin && (
            <div className="view-as-floating">
              <span className="view-as-label">System:</span>
              <Select
                selectedOption={globalSelectedSystem}
                onChange={({ detail }) => setGlobalSelectedSystem(detail.selectedOption)}
                options={[
                  { label: "All Systems", value: "All" },
                  ...ALL_SYSTEMS,
                ]}
                placeholder="System"
              />
            </div>
          )}
        </div>
      </div>

      <div className="permission-tabs-container">
        <Tabs
          activeTabId={activeTabId}
          onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
          tabs={tabs}
        />
      </div>
    </div>
  );
};

export default PermissionManagement;
